import { collectPages } from '../../../../packages/contracts/src/index.js';
import type {
  Customer,
  CustomerCreate,
  CustomerUpdate,
  Appointment,
  AppointmentCreate,
  AppointmentUpdate,
  CheckInRequest,
  DashboardSummary,
  Daycare,
  DaycareCreate,
  DaycarePhoto,
  DaycarePhotoCreate,
  DaycareUpdate,
  Dog,
  DogCreate,
  DogUpdate,
  FileUploadRequest,
  FileUploadResponse,
  FileDownloadResponse,
  Price,
  PriceCreate,
  Payment,
  PaymentCreate,
  PaymentUpdate,
  MembershipPackage,
  MembershipPackageCreate,
  MembershipPackageUpdate,
  MembershipPurchase,
  MembershipPurchaseCreate,
  MembershipPurchaseUpdate,
  MembershipEligibility,
  PublicStayGallery,
  PublicDaycareGallery,
  Service,
  ServiceCreate,
  ServiceUpdate,
  StaffRole,
  RoleModulePermissions,
  StaffUser,
  StaffUserCreate,
  StaffUserNameUpdate,
  Stay,
  StayCreate,
  StayLog,
  StayLogCreate,
  StayPhoto,
  StayPhotoCreate,
  StayUpdate,
} from '../../../../packages/contracts/src/index.js';
import { getValidAccessToken } from '../features/auth/cognito';
import { ApiRateLimitError, ensureApiRequestAllowed, isPublicApiRequest, notifyApiRateLimit, registerApiRateLimit } from './api-rate-limit';

export interface ListResult<T> {
  readonly items: T[];
  readonly cursor?: string;
}

type ListOptions = {
  readonly limit?: number;
  readonly cursor?: string;
  readonly search?: string;
  readonly q?: string;
  readonly from?: string;
  readonly to?: string;
  readonly status?: string;
  readonly category?: string;
  readonly customerId?: string;
  readonly ownerId?: string;
};

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000';

function listPath(path: string, options?: unknown) {
  const params = new URLSearchParams();
  if (typeof options === 'object' && options !== null && 'limit' in options && typeof options.limit === 'number') params.set('limit', String(options.limit));
  if (typeof options === 'object' && options !== null && 'cursor' in options && typeof options.cursor === 'string') params.set('cursor', options.cursor);
  if (typeof options === 'object' && options !== null && 'search' in options && typeof options.search === 'string' && options.search.trim()) params.set('search', options.search.trim());
  if (typeof options === 'object' && options !== null && 'q' in options && typeof options.q === 'string' && options.q.trim()) params.set('q', options.q.trim());
  if (typeof options === 'object' && options !== null && 'from' in options && typeof options.from === 'string' && options.from.trim()) params.set('from', options.from.trim());
  if (typeof options === 'object' && options !== null && 'to' in options && typeof options.to === 'string' && options.to.trim()) params.set('to', options.to.trim());
  if (typeof options === 'object' && options !== null && 'status' in options && typeof options.status === 'string' && options.status.trim()) params.set('status', options.status.trim());
  if (typeof options === 'object' && options !== null && 'category' in options && typeof options.category === 'string' && options.category.trim()) params.set('category', options.category.trim());
  if (typeof options === 'object' && options !== null && 'customerId' in options && typeof options.customerId === 'string' && options.customerId.trim()) params.set('customerId', options.customerId.trim());
  if (typeof options === 'object' && options !== null && 'dogId' in options && typeof options.dogId === 'string' && options.dogId.trim()) params.set('dogId', options.dogId.trim());
  if (typeof options === 'object' && options !== null && 'date' in options && typeof options.date === 'string' && options.date.trim()) params.set('date', options.date.trim());
  if (typeof options === 'object' && options !== null && 'preferredPurchaseId' in options && typeof options.preferredPurchaseId === 'string' && options.preferredPurchaseId.trim()) params.set('preferredPurchaseId', options.preferredPurchaseId.trim());
  if (typeof options === 'object' && options !== null && 'ownerId' in options && typeof options.ownerId === 'string' && options.ownerId.trim()) params.set('ownerId', options.ownerId.trim());
  if (typeof options === 'object' && options !== null && 'period' in options && typeof options.period === 'string' && options.period.trim()) params.set('period', options.period.trim());
  const query = params.toString();
  return `${path}${query ? `?${query}` : ''}`;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const isPublic = isPublicApiRequest(path);
  if (!isPublic) {
    try {
      ensureApiRequestAllowed();
    } catch (error) {
      if (error instanceof ApiRateLimitError) notifyApiRateLimit(path);
      throw error;
    }
  }
  const headers = new Headers(init.headers);
  headers.set('content-type', 'application/json');
  const token = await getValidAccessToken();
  if (token) headers.set('authorization', `Bearer ${token}`);

  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers,
  });

  if (response.status === 429) {
    if (!isPublic) {
      registerApiRateLimit();
      notifyApiRateLimit(path);
    }
    throw new ApiRateLimitError();
  }
  if (response.status === 204) return undefined as T;
  const payload = (await response.json()) as unknown;
  if (!response.ok) {
    const message =
      typeof payload === 'object' && payload !== null && 'error' in payload && typeof payload.error === 'object' && payload.error !== null && 'message' in payload.error
        ? String(payload.error.message)
        : 'Request failed';
    throw new Error(message);
  }
  return payload as T;
}

// Screens paginate and filter locally, so a server page is not a complete list.
async function requestList<T>(path: string): Promise<ListResult<T>> {
  const [pathname, query] = path.split('?');
  const params = new URLSearchParams(query);
  const initialCursor = params.get('cursor') ?? undefined;
  if (!params.has('limit')) params.set('limit', '100');
  const items = await collectPages<T>((cursor) => {
    if (cursor) params.set('cursor', cursor);
    else params.delete('cursor');
    return request<ListResult<T>>(`${pathname}?${params}`);
  }, initialCursor);
  return { items };
}

async function listAll<T>(listPage: (options: ListOptions) => Promise<ListResult<T>>, options: ListOptions = {}) {
  const items: T[] = [];
  let cursor = options.cursor;
  do {
    const page = await listPage({ ...options, cursor, limit: options.limit ?? 100 });
    items.push(...page.items);
    cursor = page.cursor;
  } while (cursor);
  return { items };
}

export const api = {
  dashboard: (options?: { period?: string; from?: string; to?: string }) => request<DashboardSummary>(listPath('/v1/dashboard/summary', options)),

  appointments: {
    list: (options?: ListOptions | unknown) => requestList<Appointment>(listPath('/v1/appointments', options)),
    get: (id: string) => request<Appointment>(`/v1/appointments/${id}`),
    create: (input: AppointmentCreate) => request<Appointment>('/v1/appointments', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: AppointmentUpdate) => request<Appointment>(`/v1/appointments/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    markDone: (id: string) => request<Appointment>(`/v1/appointments/${id}/done`, { method: 'POST' }),
    remove: (id: string) => request<void>(`/v1/appointments/${id}`, { method: 'DELETE' }),
  },

  customers: {
    list: (options?: ListOptions | unknown) => requestList<Customer>(listPath('/v1/customers', options)),
    listAll: (options?: ListOptions) => listAll<Customer>((pageOptions) => api.customers.list(pageOptions), options),
    get: (id: string) => request<Customer>(`/v1/customers/${id}`),
    dogs: (id: string, options?: ListOptions | unknown) => requestList<Dog>(listPath(`/v1/customers/${id}/dogs`, options)),
    allDogs: (id: string, options?: ListOptions) => listAll<Dog>((pageOptions) => api.customers.dogs(id, pageOptions), options),
    create: (input: CustomerCreate) => request<Customer>('/v1/customers', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: CustomerUpdate) => request<Customer>(`/v1/customers/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<void>(`/v1/customers/${id}`, { method: 'DELETE' }),
  },

  dogs: {
    list: (options?: ListOptions | unknown) => requestList<Dog>(listPath('/v1/dogs', options)),
    listAll: (options?: ListOptions) => listAll<Dog>((pageOptions) => api.dogs.list(pageOptions), options),
    get: (id: string) => request<Dog>(`/v1/dogs/${id}`),
    create: (input: DogCreate) => request<Dog>('/v1/dogs', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: DogUpdate) => request<Dog>(`/v1/dogs/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<void>(`/v1/dogs/${id}`, { method: 'DELETE' }),
  },

  daycares: {
    list: (options?: ListOptions | unknown) => requestList<Daycare>(listPath('/v1/daycares', options)),
    get: (id: string) => request<Daycare>(`/v1/daycares/${id}`),
    create: (input: DaycareCreate) => request<Daycare>('/v1/daycares', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: DaycareUpdate) => request<Daycare>(`/v1/daycares/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<void>(`/v1/daycares/${id}`, { method: 'DELETE' }),
    checkIn: (id: string, input: CheckInRequest = {}) => request<Daycare>(`/v1/daycares/${id}/check-in`, { method: 'POST', body: JSON.stringify(input) }),
    checkOut: (id: string) => request<Daycare>(`/v1/daycares/${id}/check-out`, { method: 'POST' }),
    addPhoto: (id: string, input: DaycarePhotoCreate) => request<DaycarePhoto>(`/v1/daycares/${id}/photos`, { method: 'POST', body: JSON.stringify(input) }),
  },

  services: {
    list: (options?: ListOptions | unknown) => requestList<Service>(listPath('/v1/services', options)),
    get: (id: string) => request<Service>(`/v1/services/${id}`),
    create: (input: ServiceCreate) => request<Service>('/v1/services', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: ServiceUpdate) => request<Service>(`/v1/services/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<void>(`/v1/services/${id}`, { method: 'DELETE' }),
    prices: (serviceId: string, options: ListOptions = {}) => requestList<Price>(listPath(`/v1/services/${serviceId}/prices`, options)),
    createPrice: (serviceId: string, input: Omit<PriceCreate, 'serviceId'>) =>
      request<Price>(`/v1/services/${serviceId}/prices`, { method: 'POST', body: JSON.stringify({ ...input, serviceId }) }),
    removePrice: (serviceId: string, priceId: string) => request<void>(`/v1/services/${serviceId}/prices/${priceId}`, { method: 'DELETE' }),
  },

  stays: {
    list: (options?: ListOptions | unknown) => requestList<Stay>(listPath('/v1/stays', options)),
    get: (id: string) => request<Stay>(`/v1/stays/${id}`),
    create: (input: StayCreate) => request<Stay>('/v1/stays', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: StayUpdate) => request<Stay>(`/v1/stays/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<void>(`/v1/stays/${id}`, { method: 'DELETE' }),
    checkIn: (id: string, input: CheckInRequest = {}) => request<Stay>(`/v1/stays/${id}/check-in`, { method: 'POST', body: JSON.stringify(input) }),
    checkOut: (id: string) => request<Stay>(`/v1/stays/${id}/check-out`, { method: 'POST' }),
    addLog: (id: string, input: StayLogCreate) => request<StayLog>(`/v1/stays/${id}/logs`, { method: 'POST', body: JSON.stringify(input) }),
    addPhoto: (id: string, input: StayPhotoCreate) => request<StayPhoto>(`/v1/stays/${id}/photos`, { method: 'POST', body: JSON.stringify(input) }),
  },

  publicGallery: (shortId: string) => request<PublicStayGallery>(`/public/stays/${shortId}/photos`),
  publicDaycareGallery: (daycareId: string) => request<PublicDaycareGallery>(`/public/daycares/${daycareId}/photos`),

  payments: {
    list: (options?: ListOptions | unknown) => requestList<Payment>(listPath('/v1/payments', options)),
    create: (input: PaymentCreate) => request<Payment>('/v1/payments', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, payment: PaymentUpdate, masterCode: string) => request<Payment>(`/v1/payments/${id}`, { method: 'PATCH', body: JSON.stringify({ payment, masterCode }) }),
    remove: (id: string, masterCode: string) => request<void>(`/v1/payments/${id}`, { method: 'DELETE', body: JSON.stringify({ masterCode }) }),
  },

  memberships: {
    packages: (options?: ListOptions | unknown) => requestList<MembershipPackage>(listPath('/v1/membership-packages', options)),
    createPackage: (input: MembershipPackageCreate) => request<MembershipPackage>('/v1/membership-packages', { method: 'POST', body: JSON.stringify(input) }),
    updatePackage: (id: string, input: MembershipPackageUpdate) => request<MembershipPackage>(`/v1/membership-packages/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    removePackage: (id: string) => request<void>(`/v1/membership-packages/${id}`, { method: 'DELETE' }),
    purchases: (options?: ListOptions | unknown) => requestList<MembershipPurchase>(listPath('/v1/membership-purchases', options)),
    createPurchase: (input: MembershipPurchaseCreate) => request<MembershipPurchase>('/v1/membership-purchases', { method: 'POST', body: JSON.stringify(input) }),
    updatePurchase: (id: string, input: MembershipPurchaseUpdate) => request<MembershipPurchase>(`/v1/membership-purchases/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    removePurchase: (id: string) => request<void>(`/v1/membership-purchases/${id}`, { method: 'DELETE' }),
    eligibility: (customerId: string, dogId: string, date: string, preferredPurchaseId?: string) => request<MembershipEligibility>(listPath('/v1/membership-eligibility', { customerId, dogId, date, preferredPurchaseId })),
  },

  files: {
    createUpload: (input: FileUploadRequest) => request<FileUploadResponse>('/v1/files/presigned-upload', { method: 'POST', body: JSON.stringify(input) }),
    createDownload: (objectKey: string) => request<FileDownloadResponse>(`/v1/files/download?objectKey=${encodeURIComponent(objectKey)}`),
  },

  staffUsers: {
    list: (options?: ListOptions | unknown) => requestList<StaffUser>(listPath('/v1/staff-users', options)),
    create: (input: StaffUserCreate) => request<StaffUser>('/v1/staff-users', { method: 'POST', body: JSON.stringify(input) }),
    updateRole: (username: string, role: StaffRole) => request<void>(`/v1/staff-users/${encodeURIComponent(username)}/role`, { method: 'PATCH', body: JSON.stringify({ role }) }),
    updateName: (username: string, input: StaffUserNameUpdate) => request<void>(`/v1/staff-users/${encodeURIComponent(username)}/name`, { method: 'PATCH', body: JSON.stringify(input) }),
    disable: (username: string) => request<void>(`/v1/staff-users/${encodeURIComponent(username)}/disable`, { method: 'POST' }),
    enable: (username: string) => request<void>(`/v1/staff-users/${encodeURIComponent(username)}/enable`, { method: 'POST' }),
  },
  staffRoles: {
    list: () => request<ListResult<StaffRole>>('/v1/staff-roles'),
    create: (role: StaffRole) => request<void>('/v1/staff-roles', { method: 'POST', body: JSON.stringify({ role }) }),
    remove: (role: StaffRole) => request<void>(`/v1/staff-roles/${encodeURIComponent(role)}`, { method: 'DELETE' }),
  },
  staffRolePermissions: {
    get: () => request<RoleModulePermissions>('/v1/staff-role-permissions'),
    update: (input: RoleModulePermissions) => request<RoleModulePermissions>('/v1/staff-role-permissions', { method: 'PUT', body: JSON.stringify(input) }),
  },
  masterCodeSettings: {
    get: () => request<{ configured: boolean }>('/v1/master-code-settings'),
    update: (masterCode: string | null) => request<{ configured: boolean }>('/v1/master-code-settings', { method: 'PUT', body: JSON.stringify({ masterCode }) }),
  },
};
